import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AppComponent } from 'src/app/app.component';
import { NodeDTO } from 'src/app/model/nodes/node-dto';
import { LoadIndicatorService } from 'src/app/services/load-indicator.service';
import { TraceabilityService } from 'src/app/services/traceability.service';
import { PaginatorComponent } from '../paginator/paginator.component';
import { NodeActionDTO } from 'src/app/model/traceability/node-action-dto';
import { ThreatManagementDTO } from 'src/app/model/traceability/threat-management-dto';
import { Utils } from 'src/app/utils/utils';
import { AlertDTO } from 'src/app/model/alerts/alert-dto';

@Component({
    selector: 'app-node-history',
    templateUrl: './node-history.component.html',
    styleUrl: './node-history.component.scss'
})
export class NodeHistoryComponent implements OnInit {
    public nodeId: number = -1
    public nodes?: NodeDTO[];
    public tabConfiguration: string = 'config'
    public tabActions: string = 'actions'
    public tabThreatments: string = 'threatments'
    public currentTab: string = this.tabConfiguration
    public nodeActionsCount: number = 0
    public nodeThreatManagementsCount: number = 0
    public nodeConfigurationsCount: number = 0
    public nodeSelectedConfigurationTimestamp: Date | undefined
    public nodeSelectedConfigurationJson: string | undefined
    public nodeSelectedAction: NodeActionDTO | undefined
    public nodeSelectedThreatManagement: ThreatManagementDTO | undefined

    utils = new Utils()

    @ViewChild('configurationsPaginator')
    public configurationsPaginator?: PaginatorComponent;
    @ViewChild('actionsPaginator')
    public actionsPaginator?: PaginatorComponent;
    @ViewChild('threatmentsPaginator')
    public threatmentsPaginator?: PaginatorComponent;

    constructor(
        private readonly traceabilityService: TraceabilityService,
        private readonly loadIndicatorService: LoadIndicatorService,
        private readonly toastr: ToastrService,
        private readonly route: ActivatedRoute
    ) {
        this.nodeId = isNaN(+(this.route.snapshot.paramMap.get('id') ?? -1)) ? -1 : +(this.route.snapshot.paramMap.get('id') ?? -1)
        const viewText = this.nodeId > -1 ? 'history: ' + this.nodeId : 'history'
        AppComponent.updateAppHeader(viewText, 'Node manager', 'node-manager')
    }

    ngOnInit(): void {
        this.loadIndicatorService.show()

        if (this.nodeId < 0) {
            this.toastr.error('There was an error. Unable to find selected node')
        }

        this.traceabilityService.getNodeConfigurationCount(this.nodeId)
            .then((n) => {
                this.nodeConfigurationsCount = n
                if (this.configurationsPaginator && n > 0) {
                    this.configurationsPaginator.updateData(n, 1)
                    this.configurationsPaginator.selectedPage = n
                    this.loadNodeConfiguration()
                }
            })
            .finally(() => { this.loadIndicatorService.hide() })

        this.traceabilityService.getNodeActionCount(this.nodeId)
            .then((n) => {
                this.nodeActionsCount = n
                if (this.actionsPaginator && n > 0) {
                    this.actionsPaginator.updateData(n, 1)
                    this.actionsPaginator.selectedPage = n
                    this.loadNodeAction()
                }
            })
            .finally(() => { this.loadIndicatorService.hide() })

        this.traceabilityService.getThreatManagementCount(this.nodeId)
            .then((n) => {
                this.nodeThreatManagementsCount = n
                if (this.threatmentsPaginator && n > 0) {
                    this.threatmentsPaginator.updateData(n, 1)
                    this.threatmentsPaginator.selectedPage = n
                    this.loadNodeThreatment()
                }
            })
            .finally(() => { this.loadIndicatorService.hide() })
    }

    loadNodeConfiguration(): void {
        if (!this.configurationsPaginator) return
        this.nodeSelectedConfigurationJson = undefined
        this.nodeSelectedConfigurationTimestamp = undefined

        this.traceabilityService.getNodeConfiguration(this.nodeId, this.configurationsPaginator?.selectedPage - 1)
            .then((conf: any) => {
                this.nodeSelectedConfigurationTimestamp = conf.timestamp
                this.nodeSelectedConfigurationJson = JSON.parse(conf.configuration)
            })
    }

    loadNodeAction(): void {
        if (!this.actionsPaginator) return
        this.nodeSelectedAction = undefined

        this.traceabilityService.getNodeAction(this.nodeId, this.actionsPaginator.selectedPage - 1)
            .then((action: NodeActionDTO) => {
                this.nodeSelectedAction = action
            })
    }

    loadNodeThreatment(): void {
        if (!this.threatmentsPaginator) return
        this.nodeSelectedThreatManagement = undefined

        this.traceabilityService.getThreatManagement(this.nodeId, this.threatmentsPaginator.selectedPage - 1)
            .then((tm: ThreatManagementDTO) => {
                this.nodeSelectedThreatManagement = tm
            })
    }

    getUsersWhoManagedAlert(alert: AlertDTO) {
        let users: string[] = [];
        alert.attacks?.forEach(attack => {
            attack.cyberAttackManagements.forEach(management => {
                if (management.user.name && management.user.surname) {
                    users.push(management.user.name?.concat(' ').concat(management.user.surname ?? ''))
                }
            })
        })
        return [...new Set(users)];
    }
}
